import { useEffect, useMemo } from 'react';
import { partGenKey, usePartsRun } from './partsRun';
import { useSubmodelFilter } from './submodelFilterStore';
import { useStepsRun } from './stepsRun';
import { useStepParts } from './stepPartsRun';
import { useAssemblySteps } from './categoryBands';
import { useImagesBusy, useImagesEpoch } from './imagesRefresh';
import { ensurePartImages, generatedUnder, loadGenerated } from './realImages';
import { usePhotographedParts } from './photographedParts';
import { usePartModels } from './partModels';
import { fittingChosen, useBook } from './assemblyStore';
import { fittingFor, useAssemblySlots } from './assemblySlots';

// How far along each STAGE is — the units a phase is made of.
//
// A phase is several questions now (phase 2 lists the parts, photographs them and
// reconstructs them; phase 3 shares the parts among the steps and cuts the steps into
// sub-steps), and the phase bar averages them. The switcher INSIDE a phase shows the same
// numbers one at a time, on the card for each view.
//
// They live here rather than in phaseProgress because both read them: the bar at the top
// of the page, and the cards in the middle of it. One definition, so a card that says a
// stage is finished and a bar that says the phase is half done are the same arithmetic.
export type Stage = {
  // 0..1.
  fraction: number;
  done: boolean;
};

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

// The supplied-parts list, read off the manual. One call (or one per page), so the only
// partial reading of it is the page counter the run reports while it streams.
export function usePartsListStage(docId: string): Stage {
  const parts = usePartsRun(docId);
  const done = parts.items.length > 0;
  return {
    done,
    fraction: done
      ? 1
      : parts.progress
        ? clamp01(parts.progress.done / parts.progress.total)
        : 0,
  };
}

// The photographs, one generated per part. Plain localStorage (no reactive store), so
// re-read whenever generation goes idle or an epoch bump signals a change.
//
// Per SUBMODEL: each colour keeps its own renders, so the coverage is counted against the
// same submodel-aware keys the panel writes.
export function usePartImagesStage(docId: string): Stage {
  const parts = usePartsRun(docId);
  const submodelFilter = useSubmodelFilter(docId);
  const imagesBusy = useImagesBusy(docId);
  const imagesEpoch = useImagesEpoch(docId);
  // The images live in a server store, not synchronous doc_state — load it so this
  // reflects them without the panel being opened. Idempotent; the fetch bumps the epoch,
  // which recomputes the coverage below.
  useEffect(() => {
    if (docId) void ensurePartImages(docId);
  }, [docId]);

  const searchKeys = useMemo(
    () => parts.items.map((p) => partGenKey(p, submodelFilter)).filter(Boolean) as string[],
    [parts.items, submodelFilter],
  );
  const covered = useMemo(() => {
    if (!docId || !searchKeys.length) return 0;
    const generated = loadGenerated(docId);
    return searchKeys.filter((k) => generatedUnder(generated, k).length > 0).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, searchKeys, imagesBusy, imagesEpoch]);

  return {
    fraction: searchKeys.length ? clamp01(covered / searchKeys.length) : 0,
    done: searchKeys.length > 0 && covered === searchKeys.length,
  };
}

// A 3D model per part, in the Parts DB. Not "a mesh was reconstructed": every attempt is
// kept, good and bad, and a part is done when somebody looked at one and switched it on,
// which is what the assembly phase will read.
//
// Measured against the parts the 3D view LISTS — the ones the database holds a photograph
// of — so this and the selector's dots count the same things. A part with no photograph is
// the image stage's business and is not work owed here.
//
// WHILE THE PARTS DB HAS NOT ANSWERED — the first read, or a re-read after a switch — the last
// measure of this manual stands: counted over an unanswered list every part is unmodelled, and
// the bar emptied for a round trip every time a mesh was kept.
const lastModelsStage = new Map<string, Stage>();

export function usePartModelsStage(docId: string): Stage {
  const submodelFilter = useSubmodelFilter(docId);
  const imagesEpoch = useImagesEpoch(docId);
  const { parts, loaded } = usePhotographedParts(docId, submodelFilter, imagesEpoch);
  const partModels = usePartModels();
  if (!partModels.loaded) return lastModelsStage.get(docId) ?? { fraction: 0, done: false };
  const modelled = parts.filter((p) => partModels.itemIds.has(p.itemId)).length;
  const measured = {
    fraction: parts.length ? clamp01(modelled / parts.length) : 0,
    done: loaded && parts.length > 0 && modelled === parts.length,
  };
  lastModelsStage.set(docId, measured);
  return measured;
}

// The repartition: which parts each assembly step uses. ONE call over the whole manual,
// so it is one unit — it has landed or it has not.
export function useAllocStage(docId: string): Stage {
  const alloc = useStepParts(docId);
  const done = Object.keys(alloc.byStep).length > 0;
  return { done, fraction: done ? 1 : 0 };
}

// The step analysis: one call per assembly step. A step counts as analysed as soon as any
// of its result maps has an entry — a step whose analysis yields no sub-step would
// otherwise leave the stage forever unfinished.
export function useSubstepsStage(docId: string): Stage {
  const st = useStepsRun(docId);
  const { steps } = useAssemblySteps(docId);
  const analyzed = steps.filter(
    (s) => st.subSteps[s.category] || st.instructions[s.category],
  ).length;
  return {
    fraction: steps.length ? clamp01(analyzed / steps.length) : 0,
    done: steps.length > 0 && analyzed === steps.length,
  };
}

// Phase 7's animations: one signed-off fitting per sub-step. The phase bar and the card over
// the animations read this one number — see Phase 7 in phaseProgress, which argues the count.
//
// COUNTED OVER THE SLOTS AND NOT OVER THE BOOK'S KEYS, so both halves of the fraction are about
// the same things: a stored answer for a slot that no longer exists would make it read full one
// short. And a REPEAT is signed off where its gesture is — six sub-steps making one movement
// share one fitting (see Slot.repeat), so fittingFor resolves each slot to the answer that
// speaks for it, the same function the clips are published through.
export function useAnimationsStage(docId: string): Stage {
  const assembly = useBook(docId);
  const slots = useAssemblySlots(docId);
  const signedOff = slots.filter((slot) => {
    const f = fittingFor(assembly, slot);
    return f.validated && fittingChosen(f);
  }).length;
  return {
    fraction: slots.length ? clamp01(signedOff / slots.length) : 0,
    done: slots.length > 0 && signedOff >= slots.length,
  };
}

